import { Component, EventEmitter, OnInit, Output, computed, effect, inject, signal, untracked, ChangeDetectionStrategy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { forkJoin } from 'rxjs';
import { ApiService } from '../../../core/services/api.service';
import { AgentPromptService } from './agent-admin/prompts/agent-prompt.service';
import { DeckAdminService } from './deck-admin.service';
import { CrmProductRow, DeckGroup, DeckSlide } from './deck-admin.models';
import { PaginationState, SlideSelectionState } from './deck-list-state';
import { FOR_ENV_OPTIONS, envIncludesProduction, forEnvBadgeClass, forEnvLabel } from './for-env.util';
import { productStamp, resolveProductRow, toProductContext } from './selected-product.util';
import {
  CategoryFormPanelComponent,
  CategoryPanelMode,
} from './components/category-form-panel/category-form-panel.component';
import {
  PanelMode,
  SlideUploadPanelComponent,
} from './components/slide-upload-panel/slide-upload-panel.component';
import { SlidePreviewModalComponent } from './components/slide-preview-modal/slide-preview-modal.component';
import { DeckDeleteConfirmModalComponent } from './components/delete-confirm-modal/delete-confirm-modal.component';
import { PaginationComponent } from '../../../shared/components/pagination/pagination.component';

/**
 * Deck board: CATEGORIES on the left, the selected category's SLIDES on the right.
 * Rendered as the "Deck" tab of {@link DeckAdminComponent}.
 *
 * A category is the unit the AI addresses — `show_deck('spa_overview')` opens it at page
 * one and `next_slide` walks its pages and stops at the last. So the category, not the
 * slide, owns "when to open me", the industry it belongs to, and whether it opens the
 * call. Slides inside it are ordered by `position`, and their keys only have to be unique
 * within the category.
 *
 * PRODUCT SCOPE — same pattern as monthly-report-1: the page reacts to the CRM's global
 * product selection (sidebar → `ApiService.selectedProductId`), resolves it against
 * `GET /product/list-product`, and stamps the product's `keyname`/`pid` on every
 * deck-admin request so each product gets its own deck.
 */
@Component({
  selector: 'app-deck-board',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    CategoryFormPanelComponent,
    SlideUploadPanelComponent,
    SlidePreviewModalComponent,
    DeckDeleteConfirmModalComponent,
    PaginationComponent,
  ],
  changeDetection: ChangeDetectionStrategy.Eager,
  templateUrl: './deck-board.component.html',
})
export class DeckBoardComponent implements OnInit {
  private deckService = inject(DeckAdminService);
  private apiService = inject(ApiService);
  /** Scoped here too: the category form's agent multi-select lists this product's prompts. */
  private agentPromptService = inject(AgentPromptService);

  /** Emits the category count after each load so the tab shell can badge it. */
  @Output() countChange = new EventEmitter<number>();

  // Categories
  groups = signal<DeckGroup[]>([]);
  deckMeta = signal<{ deck: string; count: number } | null>(null);
  loading = signal(false);
  errorMsg = signal('');
  includeInactive = signal(true);
  industries = signal<string[]>([]);

  // The selected category's slides
  selectedGroupKey = signal<string | null>(null);
  pages = signal<DeckSlide[]>([]);
  pagesLoading = signal(false);

  // Panels
  categoryPanelOpen = signal(false);
  categoryPanelMode = signal<CategoryPanelMode>('create');
  selectedForEdit = signal<DeckGroup | null>(null);

  slidePanelOpen = signal(false);
  slidePanelMode = signal<PanelMode>('create');
  selectedSlide = signal<DeckSlide | null>(null);

  previewSlide = signal<DeckSlide | null>(null);

  // Delete
  deleteGroupTarget = signal<DeckGroup | null>(null);
  deleteSlideTarget = signal<DeckSlide | null>(null);
  deleting = signal(false);
  deleteSelectedSlidesConfirm = signal(false);

  // Environment move for a category + its slides, pending the confirm dialog.
  envChangeTarget = signal<{ group: DeckGroup; to: number } | null>(null);
  changingEnv = signal(false);

  // Env selector helpers for the template.
  readonly envOptions = FOR_ENV_OPTIONS;
  readonly envLabel = forEnvLabel;
  readonly envBadgeClass = forEnvBadgeClass;
  readonly envIncludesProduction = envIncludesProduction;

  countryBadge(val: unknown): string {
    if (!val) return '';
    if (Array.isArray(val)) {
      if (!val.length) return '';
      if (val.length === 1) return String(val[0]);
      if (val.length === 2) return `${val[0]}, ${val[1]}`;
      return `${val[0]} +${val.length - 1}`;
    }
    if (typeof val === 'string') {
      const s = val.trim();
      if (!s) return '';
      if (s.startsWith('[')) {
        try {
          const parsed = JSON.parse(s);
          if (Array.isArray(parsed)) return this.countryBadge(parsed);
        } catch {}
      }
      return s;
    }
    return '';
  }

  countryTooltip(val: unknown): string {
    if (!val) return '';
    if (Array.isArray(val)) return val.join(', ');
    if (typeof val === 'string') {
      const s = val.trim();
      if (s.startsWith('[')) {
        try {
          const parsed = JSON.parse(s);
          if (Array.isArray(parsed)) return parsed.join(', ');
        } catch {}
      }
      return s;
    }
    return '';
  }

  // Pagination & bulk selection
  readonly categoriesPager = new PaginationState(10);
  readonly slidesPager = new PaginationState(10);
  readonly slideSelection = new SlideSelectionState();

  paginatedGroups = computed(() => this.categoriesPager.slice(this.groups()));
  paginatedSlides = computed(() => this.slidesPager.slice(this.pages()));

  // Product scope
  /** Flips once /product/list-product has answered (or failed) — gates the first load. */
  private productsReady = signal(false);
  /** `keyname|pid` of the last applied product; undefined = nothing applied yet. */
  private appliedProductStamp: string | undefined;

  /** The globally selected product (sidebar), resolved against the CRM product list. */
  activeProduct = computed<CrmProductRow | null>(() =>
    resolveProductRow(
      (this.apiService.products() ?? []) as CrmProductRow[],
      this.apiService.selectedProductId()
    )
  );

  selectedGroup = computed(
    () => this.groups().find((g) => g.group === this.selectedGroupKey()) ?? null
  );
  introGroup = computed(() => this.groups().find((g) => g.isIntro) ?? null);
  totalPages = computed(() => this.groups().reduce((n, g) => n + (g.pageCount ?? 0), 0));

  constructor() {
    // Reload the deck whenever the global product changes (and once on boot, after
    // the product list has resolved so the very first load is already scoped).
    // The Node deck API requires a product on every route, so when the global
    // selection resolves to nothing we fall back to the first CRM product.
    effect(() => {
      if (!this.productsReady()) return;
      const product = this.activeProduct();
      untracked(() => this.applyProductScope(product));
    });
  }

  ngOnInit(): void {
    this.apiService.fetchProducts().subscribe({
      next: () => this.productsReady.set(true),
      // Still flip the gate on failure so the effect can surface the error state.
      error: () => this.productsReady.set(true),
    });
    this.deckService.listIndustries().subscribe({
      next: (keys) => this.industries.set(keys),
      // A missing dropdown is survivable; a wrong hardcoded one is not. Stay empty.
      error: () => this.industries.set([]),
    });
  }

  /** Point the service at `product`'s deck and reload. No-op if the scope is unchanged. */
  private applyProductScope(product: CrmProductRow | null): void {
    const stamp = productStamp(product);
    if (stamp === this.appliedProductStamp) return;
    this.appliedProductStamp = stamp;
    if (!product) {
      this.errorMsg.set('No products available — the deck admin needs a product to manage.');
      return;
    }
    this.deckService.setProductContext(toProductContext(product));
    this.agentPromptService.setProductContext(toProductContext(product));
    // The previous product's selection means nothing in the new deck.
    this.selectedGroupKey.set(null);
    this.pages.set([]);
    this.categoriesPager.reset();
    this.slidesPager.reset();
    this.slideSelection.clear();
    this.loadGroups();
  }

  loadGroups(): void {
    this.loading.set(true);
    this.errorMsg.set('');
    this.deckService.listGroups(this.includeInactive()).subscribe({
      next: (res) => {
        this.groups.set(res.groups ?? []);
        this.deckMeta.set({ deck: res.deck, count: res.count });
        this.countChange.emit(res.count ?? res.groups?.length ?? 0);
        this.loading.set(false);

        // Keep the selection if it survived the reload; otherwise fall to the first.
        const current = this.selectedGroupKey();
        const stillThere = current && res.groups.some((g) => g.group === current);
        const next = stillThere ? current : res.groups[0]?.group ?? null;
        if (next !== current) this.selectedGroupKey.set(next);
        if (next) {
          this.loadPages(next);
          // Land the category list on the page that holds the selection.
          this.categoriesPager.goToIndex(res.groups.findIndex((g) => g.group === next));
        } else {
          this.pages.set([]);
        }
      },
      error: (err) => {
        this.errorMsg.set(err?.message ?? 'Failed to load categories.');
        this.loading.set(false);
      },
    });
  }

  loadPages(groupKey: string): void {
    this.pagesLoading.set(true);
    this.slideSelection.clear();
    this.slidesPager.reset();
    this.deckService.listPages(groupKey).subscribe({
      next: (res) => {
        this.pages.set(res.slides ?? []);
        this.pagesLoading.set(false);
      },
      error: (err) => {
        this.pages.set([]);
        this.pagesLoading.set(false);
        this.errorMsg.set(err?.message ?? 'Failed to load slides.');
      },
    });
  }

  selectGroup(group: DeckGroup): void {
    if (group.group === this.selectedGroupKey()) return;
    this.slideSelection.clear();
    this.selectedGroupKey.set(group.group);
    this.loadPages(group.group);
  }

  // ── slide selection (bulk delete) ────────────────────────────────────────

  isAllSlidesSelected(): boolean {
    return this.slideSelection.isAllSelected(this.paginatedSlides());
  }

  toggleSelectAllSlides(event: Event): void {
    const checked = (event.target as HTMLInputElement).checked;
    this.slideSelection.setAll(this.paginatedSlides(), checked);
  }

  toggleSelectSlide(key: string, event: Event): void {
    const checked = (event.target as HTMLInputElement).checked;
    this.slideSelection.toggle(key, checked);
  }

  confirmDeleteSelectedSlides(): void {
    this.deleteSelectedSlidesConfirm.set(true);
  }

  toggleIncludeInactive(): void {
    this.includeInactive.set(!this.includeInactive());
    this.loadGroups();
  }

  // ── panels ───────────────────────────────────────────────────────────────

  openCategoryPanel(mode: CategoryPanelMode, group: DeckGroup | null): void {
    this.categoryPanelMode.set(mode);
    this.selectedForEdit.set(group);
    this.categoryPanelOpen.set(true);
  }

  closeCategoryPanel(): void {
    this.categoryPanelOpen.set(false);
    this.selectedForEdit.set(null);
  }

  onCategorySaved(): void {
    this.closeCategoryPanel();
    this.loadGroups();
  }

  openSlidePanel(mode: PanelMode, slide: DeckSlide | null): void {
    this.slidePanelMode.set(mode);
    this.selectedSlide.set(slide);
    this.slidePanelOpen.set(true);
  }

  closeSlidePanel(): void {
    this.slidePanelOpen.set(false);
    this.selectedSlide.set(null);
  }

  onSlideSaved(): void {
    this.closeSlidePanel();
    this.loadGroups(); // page counts moved, so refresh both panes
  }

  openPreview(slide: DeckSlide): void {
    this.previewSlide.set(slide);
  }

  // ── writes ───────────────────────────────────────────────────────────────

  toggleActive(slide: DeckSlide): void {
    const groupKey = this.selectedGroupKey();
    if (!groupKey) return;
    this.deckService.patchPage(groupKey, slide.key, { is_active: !slide.isActive }).subscribe({
      next: () => this.loadPages(groupKey),
      error: (err) => this.errorMsg.set(err?.message ?? 'Could not update active status.'),
    });
  }

  /** Category env selector changed — hold the move until the user confirms it. */
  requestGroupEnvChange(group: DeckGroup, to: number | string): void {
    const target = Number(to);
    if (!target || target === group.forEnv) return;
    this.envChangeTarget.set({ group, to: target });
  }

  /** Reload so the card's selector snaps back to the unchanged value. */
  cancelGroupEnvChange(): void {
    this.envChangeTarget.set(null);
    this.loadGroups();
  }

  /** Move the category AND its slides — the backend cascades for_env to the pages. */
  executeGroupEnvChange(): void {
    const change = this.envChangeTarget();
    if (!change) return;
    this.changingEnv.set(true);
    this.deckService.patchGroup(change.group.group, { for_env: change.to }).subscribe({
      next: () => {
        this.changingEnv.set(false);
        this.envChangeTarget.set(null);
        this.loadGroups();
      },
      error: (err) => {
        this.changingEnv.set(false);
        this.envChangeTarget.set(null);
        this.errorMsg.set(err?.message ?? 'Environment change failed.');
        this.loadGroups();
      },
    });
  }

  confirmDeleteGroup(group: DeckGroup): void {
    this.deleteGroupTarget.set(group);
  }

  confirmDeleteSlide(slide: DeckSlide): void {
    this.deleteSlideTarget.set(slide);
  }

  cancelDelete(): void {
    this.deleteGroupTarget.set(null);
    this.deleteSlideTarget.set(null);
    this.deleteSelectedSlidesConfirm.set(false);
  }

  executeDelete(): void {
    const group = this.deleteGroupTarget();
    const slide = this.deleteSlideTarget();
    const bulkSlides = this.deleteSelectedSlidesConfirm();
    const groupKey = this.selectedGroupKey();
    this.deleting.set(true);

    const done = {
      next: () => {
        this.deleting.set(false);
        if (bulkSlides) this.slideSelection.clear();
        this.cancelDelete();
        this.loadGroups();
      },
      error: (err: any) => {
        this.deleting.set(false);
        this.cancelDelete();
        this.errorMsg.set(err?.message ?? 'Delete failed.');
      },
    };

    if (group) {
      // The selected category is about to vanish; let loadGroups pick a new one.
      if (group.group === groupKey) this.selectedGroupKey.set(null);
      this.deckService.deleteGroup(group.group).subscribe(done);
    } else if (slide && groupKey) {
      this.deckService.deletePage(groupKey, slide.key).subscribe(done);
    } else if (bulkSlides && groupKey) {
      const requests = this.slideSelection
        .toArray()
        .map((key) => this.deckService.deletePage(groupKey, key));
      forkJoin(requests).subscribe(done);
    } else {
      this.deleting.set(false);
      this.cancelDelete();
    }
  }

  // ── trackBy ──────────────────────────────────────────────────────────────

  trackByGroup(_: number, group: DeckGroup): string {
    return group.group;
  }

  trackBySlide(_: number, slide: DeckSlide): string {
    return slide.key;
  }
}
